import { Directive } from '@angular/core';
import { CompanyUpdateService } from './services/company-update.service.js';
import {
	Company,
	ICompanyUpdate,
	EditItemType,
	DiversityOption,
} from 'company-finder-common';
import { ComponentBase } from '@Common';
import {
	ReviewableUpdate,
	SelfUpdateMode,
} from './company-update.interface.js';
import { CompanyService } from '@Common';
import { DeploymentContext } from '@Common';

@Directive()
export abstract class UpdateComponentBase extends ComponentBase {
	constructor(
		dc: DeploymentContext,
		public updateService: CompanyUpdateService,
		public companyService: CompanyService
	) {
		super(dc);
	}

	// Expose Enums so they can be used directly in templates
	public SelfUpdateModes = SelfUpdateMode;
	public EditItemType = EditItemType;

	// Similarly, the service calls really clutter up the templates.
	// Rather than make them there, give all update components access
	// to the common ones.
	public get company(): Company {
		return this.updateService.company;
	}

	public set company(value: Company) {
		this.updateService.company = value;
	}

	public get companyBeforeAnyChanges(): Company {
		return this.updateService.companyBeforeAnyChanges;
	}

	public set companyBeforeAnyChanges(value: Company) {
		this.updateService.companyBeforeAnyChanges = value;
	}

	public get companyClone(): Company {
		return this.updateService.companyClone;
	}

	public set companyClone(value: Company) {
		this.updateService.companyClone = value;
	}

	public get companyWithPending(): Company {
		return this.updateService.companyWithPending;
	}

	public set companyWithPending(value: Company) {
		this.updateService.companyWithPending = value;
	}
	public get companyWithMostRecentEdit(): Company {
		return this.updateService.companyWithMostRecentEdit;
	}

	public set companyWithMostRecentEdit(value: Company) {
		this.updateService.companyWithMostRecentEdit = value;
	}

	public get isEditingProperty(): { [propName: string]: boolean } {
		return this.updateService.isEditingProperty;
	}

	public get updateForReview(): ReviewableUpdate {
		return this.updateService.updateForReview;
	}

	public set updateForReview(value: ReviewableUpdate) {
		this.updateService.updateForReview = value;
	}

	public get reviewComments(): string {
		return this.updateService.reviewComments;
	}

	public set reviewComments(value: string) {
		this.updateService.reviewComments = value;
	}

	public get selfUpdateMode(): SelfUpdateMode {
		return this.updateService.selfUpdateMode;
	}

	public set selfUpdateMode(value: SelfUpdateMode) {
		this.updateService.selfUpdateMode = value;
	}

	public get inEditMode(): boolean {
		return this.updateService.selfUpdateMode === SelfUpdateMode.Edit;
	}

	public get iReviewMode(): boolean {
		return this.updateService.selfUpdateMode === SelfUpdateMode.Review;
	}

	public get haveDealsChanged(): boolean {
		return this.updateService.haveDealsChanged;
	}

	public set haveDealsChanged(value: boolean) {
		this.updateService.haveDealsChanged = value;
	}

	public get haveFundingsChanged(): boolean {
		return this.updateService.haveFundingsChanged;
	}

	public set haveFundingsChanged(value: boolean) {
		this.updateService.haveFundingsChanged = value;
	}

	public get isShareWithFollowers(): boolean {
		return this.updateService.isShareWithFollowers;
	}

	public set isShareWithFollowers(value: boolean) {
		this.updateService.isShareWithFollowers = value;
	}

	public isUpdateFieldSet(field: string): boolean {
		return this.updateService.isUpdateFieldSet(field);
	}

	public get isProgressUpdateSet(): boolean {
		return this.updateService.isProgressUpdateSet();
	}

	public get pendingUpdate(): ICompanyUpdate {
		return this.updateService.pendingUpdate;
	}

	public set pendingUpdate(value: ICompanyUpdate) {
		this.updateService.pendingUpdate = value;
	}

	// Note: These values need to be kept in sync with the configuration data. If it changes,
	// for example, to just 'Other' then our code breaks. We could, instead, rely on
	// assumed positioning (second to last and last), but there's no simple way to add
	// a comment to the JSON file reminding maintainers to honor that contract.
	protected OTHER_COUNTRY_STRING = 'Other Country';
	protected ALL_COUNTRIES_STRING = 'All Countries';
	protected getDiversityOptionsForCountry(
		countryForDeiReporting: string
	): DiversityOption[] {
		if (!countryForDeiReporting) {
			return undefined;
		}

		const deiCountriesAndOptions =
			this._deploymentContext.referenceValueData
				.deiCountriesAndOptionsList;

		let deiConfig = deiCountriesAndOptions.find(
			(item) => item.label === countryForDeiReporting
		);

		if (!deiConfig) {
			// Couldn't find a set for the specific country, so fall back on the "Other Country" set
			deiConfig = deiCountriesAndOptions.find(
				(item) => item.label === this.OTHER_COUNTRY_STRING // See comment below regarding OTHER_COUNTRY_STRING
			);
		}

		// Get the options that should be included for all countries
		const allCountriesDeiConfig = deiCountriesAndOptions.find(
			(item) => item.label === this.ALL_COUNTRIES_STRING // See comment below regarding ALL_COUNTRIES_STRING
		);

		// Return the combination of the two sets
		return deiConfig?.diversityOptions.concat(
			allCountriesDeiConfig?.diversityOptions
		);
	}
}
